import React, { useState, useEffect } from 'react';
import { Box, Skeleton } from '@mui/material';
import { ChartSkeleton, DetailSkeleton, TableSkeleton, FilterBarSkeleton } from '../skeletons';
import { resolveSkeletonSpec } from '../skeletons/skeletonSpec';
import { getDevFlag } from '../../constants/devFlags';

/**
 * DataSection - Wrapper component for section-level loading states
 *
 * Shows a skeleton placeholder while data is loading, then fades in the content
 * when data is ready. Designed to not interfere with children's layout/height.
 *
 * @param {boolean} isLoading - Whether the section is still loading
 * @param {object} skeletonSpec - Descriptor-driven skeleton spec. When provided, it wins over legacy skeleton props.
 * @param {string} skeletonType - Type of skeleton: 'chart' | 'table' | 'filter' | 'splitChartTable'
 * @param {object} skeletonProps - Props to pass to the skeleton component
 * @param {number} minHeight - Minimum height for skeleton (prevents layout shift)
 * @param {number} fadeDelay - Delay before fade-in starts in ms (default: 0)
 * @param {number} fadeDuration - Duration of fade-in animation in ms (default: 300)
 * @param {React.ReactNode} children - The content to display when loaded
 */
const DataSection = ({
  isLoading,
  skeletonSpec,
  skeletonType = 'chart',
  skeletonProps = {},
  minHeight,
  fadeDelay = 50,
  fadeDuration = 500,
  children,
}) => {
  const forceSkeletons = getDevFlag('FORCE_SKELETON');
  const skeletonDelay = getDevFlag('SKELETON_DELAY_MS');
  const [delayActive, setDelayActive] = useState(() => Boolean(skeletonDelay && !isLoading && !forceSkeletons));
  const resolvedSkeleton = resolveSkeletonSpec({
    skeletonSpec,
    skeletonType,
    skeletonProps,
    minHeight,
  });

  const renderSkeletonHeader = () => {
    if (!resolvedSkeleton.header) return null;
    const {
      actions = true,
      maxWidth = '100%',
      width = '100%',
    } = resolvedSkeleton.header;

    return (
      <Box
        data-testid="data-section-skeleton-header"
        data-skeleton-header-actions={actions ? 'true' : 'false'}
        data-skeleton-header-width={String(width)}
        sx={{
          width,
          maxWidth,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 0.25,
          mb: 0.5,
          px: 0.25,
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1, flexWrap: 'wrap', minHeight: 26 }}>
          <Box sx={{ minWidth: 0, flex: '0 1 auto' }}>
            <Skeleton variant="text" width={180} height={20} />
            <Skeleton variant="text" width={260} height={14} />
          </Box>
          {actions && (
            <Box data-testid="data-section-skeleton-header-actions" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.75, flex: '0 1 auto', flexWrap: 'wrap' }}>
              {[...Array(3)].map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rounded"
                  width={index === 0 ? 74 : 58}
                  height={24}
                  sx={{ borderRadius: '4px' }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  useEffect(() => {
    if (!skeletonDelay || isLoading || forceSkeletons) return;
    setDelayActive(true);
    const timer = setTimeout(() => setDelayActive(false), skeletonDelay);
    return () => clearTimeout(timer);
  }, [isLoading, skeletonDelay, forceSkeletons]);
  const renderSkeleton = () => {
    switch (resolvedSkeleton.kind) {
      case 'table':
        return <TableSkeleton {...resolvedSkeleton.props} />;
      case 'filter':
        return <FilterBarSkeleton {...resolvedSkeleton.props} />;
      case 'detail':
        return <DetailSkeleton {...resolvedSkeleton.props} />;
      case 'splitChartTable':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', alignItems: 'center', gap: 2, height: '100%' }}>
            <ChartSkeleton {...(resolvedSkeleton.props.chart || {})} />
            <TableSkeleton {...(resolvedSkeleton.props.table || {})} />
          </Box>
        );
      case 'chart':
      default:
        return <ChartSkeleton {...resolvedSkeleton.props} />;
    }
  };

  // Show skeleton while loading (or forced via dev flag)
  if (forceSkeletons || isLoading || delayActive) {
    return (
      <Box sx={{ minHeight: resolvedSkeleton.minHeight, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {renderSkeletonHeader()}
        {renderSkeleton()}
      </Box>
    );
  }

  // When loaded, render children directly with CSS fade animation
  // Using display: contents so this wrapper doesn't affect flex layout
  return (
    <>
      <style>
        {`@keyframes dataSectionFadeIn { from { opacity: 0; } to { opacity: 1; } }`}
      </style>
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            style: {
              ...child.props.style,
              animation: `dataSectionFadeIn ${fadeDuration}ms ease-out`,
              animationDelay: `${fadeDelay}ms`,
              animationFillMode: 'both',
            },
          });
        }
        return child;
      })}
    </>
  );
};

export default DataSection;
